import { useMemo, useState } from "react";
import { motion } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { scrollToSection } from "@/lib/scroll";

const EASE = [0.16, 1, 0.3, 1];

const TYPES = [
  { id: "domestic", label: "Regular Domestic", testid: "calc-type-domestic" },
  { id: "deep", label: "Deep Clean", testid: "calc-type-deep" },
  { id: "tenancy", label: "End of Tenancy", testid: "calc-type-tenancy" },
];

const FREQUENCIES = [
  { id: "one-off", label: "One-off", mult: 1 },
  { id: "fortnightly", label: "Fortnightly", mult: 0.95 },
  { id: "weekly", label: "Weekly", mult: 0.9 },
];

const ADDONS = [
  { id: "oven", label: "Oven detailing", price: 55, testid: "calc-addon-oven" },
  { id: "windows", label: "Interior windows", price: 35, testid: "calc-addon-windows" },
  { id: "carpets", label: "Carpet refresh", price: 45, testid: "calc-addon-carpets" },
];

const TYPE_LABEL = {
  domestic: "Regular Domestic Clean",
  deep: "Deep Clean",
  tenancy: "End of Tenancy Clean",
};

export default function PricingCalculator({ onBook }) {
  const [type, setType] = useState("domestic");
  const [bedrooms, setBedrooms] = useState(2);
  const [frequency, setFrequency] = useState("weekly");
  const [addons, setAddons] = useState([]);

  const estimate = useMemo(() => {
    let base;
    if (type === "domestic") {
      const hours = 2 + 0.5 * (bedrooms - 1);
      base = hours * 18 * FREQUENCIES.find((f) => f.id === frequency).mult;
    } else if (type === "deep") {
      base = Math.min(200, 160 + 13 * (bedrooms - 1));
    } else {
      base = Math.min(260, 230 + 7 * (bedrooms - 1));
    }
    base += addons.reduce((sum, id) => sum + ADDONS.find((a) => a.id === id).price, 0);
    return { low: Math.round(base), high: Math.round(base * 1.05) };
  }, [type, bedrooms, frequency, addons]);

  const toggleAddon = (id) =>
    setAddons((prev) => (prev.includes(id) ? prev.filter((a) => a !== id) : [...prev, id]));

  const estimateText = `£${estimate.low} – £${estimate.high} (${TYPE_LABEL[type]}, ${bedrooms} bed${
    type === "domestic" ? `, ${frequency}` : ""
  }${addons.length ? `, +${addons.length} add-on${addons.length > 1 ? "s" : ""}` : ""})`;

  const pill = (active) =>
    `rounded-full border px-5 py-2.5 text-sm font-medium transition-[background-color,color,border-color] duration-300 ${
      active
        ? "border-[#D4AF37] bg-[#D4AF37] text-[#0B1D16]"
        : "border-[#E5DEC3] bg-white/60 text-[#4A5A52] hover:border-[#D4AF37]/60 hover:text-[#0B1D16]"
    }`;

  return (
    <section id="pricing" data-testid="pricing-calculator" className="bg-[#0B1D16] py-24 lg:py-36">
      <div className="mx-auto grid max-w-7xl grid-cols-1 gap-16 px-6 lg:grid-cols-12 lg:px-10">
        <motion.div
          initial={{ opacity: 0, y: 32 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.9, ease: EASE }}
          className="lg:col-span-5"
        >
          <p className="mb-4 font-mono text-xs uppercase tracking-[0.25em] text-[#D4AF37]">
            Instant Estimate
          </p>
          <h2 className="font-serif text-4xl font-light leading-[1.05] tracking-tight text-[#FAF8F5] sm:text-5xl">
            Know the figure
            <br />
            <em className="italic text-[#D4AF37]">before we knock.</em>
          </h2>
          <p className="mt-6 max-w-sm text-base leading-relaxed text-[#FAF8F5]/60">
            Three choices, thirty seconds. Your estimate is indicative — we
            confirm the final quote after a quick chat.
          </p>

          <div className="mt-10 rounded-[1.75rem] border border-[#1E3B30] bg-[#0F382A]/60 p-8">
            <p className="font-mono text-[11px] uppercase tracking-[0.25em] text-[#FAF8F5]/50">
              Your estimate
            </p>
            <p
              data-testid="calc-estimate-value"
              className="mt-3 font-serif text-5xl font-light text-[#FAF8F5] lg:text-6xl"
            >
              £{estimate.low}
              <span className="text-[#FAF8F5]/40"> – </span>£{estimate.high}
            </p>
            <p className="mt-3 text-sm text-[#FAF8F5]/50">
              {TYPE_LABEL[type]} · {bedrooms} bedroom{bedrooms > 1 ? "s" : ""}
              {type === "domestic" ? ` · ${frequency}` : ""}
            </p>
            <button
              data-testid="calc-book-button"
              onClick={() => {
                onBook({ service: TYPE_LABEL[type], estimate: estimateText });
                scrollToSection("#booking");
              }}
              className="group mt-8 flex items-center gap-2 rounded-full bg-[#D4AF37] px-7 py-3.5 text-sm font-semibold text-[#0B1D16] transition-[background-color,transform] duration-300 hover:-translate-y-0.5 hover:bg-[#e3c153]"
            >
              Reserve this estimate
              <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
            </button>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 32 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.9, delay: 0.12, ease: EASE }}
          className="space-y-10 lg:col-span-7 lg:pt-24"
        >
          <div>
            <p className="mb-4 font-mono text-[11px] uppercase tracking-[0.25em] text-[#FAF8F5]/50">
              01 — Type of clean
            </p>
            <div className="flex flex-wrap gap-3">
              {TYPES.map((t) => (
                <button
                  key={t.id}
                  data-testid={t.testid}
                  onClick={() => setType(t.id)}
                  className={pill(type === t.id)}
                >
                  {t.label}
                </button>
              ))}
            </div>
          </div>

          <div>
            <p className="mb-4 font-mono text-[11px] uppercase tracking-[0.25em] text-[#FAF8F5]/50">
              02 — Bedrooms
            </p>
            <div className="flex flex-wrap gap-3">
              {[1, 2, 3, 4, 5].map((n) => (
                <button
                  key={n}
                  data-testid={`calc-bedrooms-${n}`}
                  onClick={() => setBedrooms(n)}
                  className={`${pill(bedrooms === n)} h-12 w-12 px-0`}
                >
                  {n}
                </button>
              ))}
            </div>
          </div>

          {type === "domestic" && (
            <div>
              <p className="mb-4 font-mono text-[11px] uppercase tracking-[0.25em] text-[#FAF8F5]/50">
                03 — Frequency
              </p>
              <div className="flex flex-wrap gap-3">
                {FREQUENCIES.map((f) => (
                  <button
                    key={f.id}
                    data-testid={`calc-frequency-${f.id}`}
                    onClick={() => setFrequency(f.id)}
                    className={pill(frequency === f.id)}
                  >
                    {f.label}
                  </button>
                ))}
              </div>
            </div>
          )}

          <div>
            <p className="mb-4 font-mono text-[11px] uppercase tracking-[0.25em] text-[#FAF8F5]/50">
              {type === "domestic" ? "04" : "03"} — Finishing touches
            </p>
            <div className="flex flex-wrap gap-3">
              {ADDONS.map((a) => (
                <button
                  key={a.id}
                  data-testid={a.testid}
                  onClick={() => toggleAddon(a.id)}
                  className={pill(addons.includes(a.id))}
                >
                  {a.label} · +£{a.price}
                </button>
              ))}
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
